import { STRING, FLOAT, INTEGER, BOOLEAN, GEOMETRY, DATE, DATETIME, DECIMAL  } from './attribute_type';
import { getGeometryType } from './geometry_type';

// http://www.geopackage.org/spec130/#_sqlite_container 1.1.1.1.1. File Format
const GEOPACKAGE_APPLICATION_ID = 0x47504B47;
const GEOPACKAGE_USER_VERSION = 1;

const wasmUrl = new URL('sql-wasm/dist/sqlite3.wasm', import.meta.url);

class FeatureProperty {

	constructor(row) {
		this.name = row[1];
		this.property_type = getPropertyType(row[2]);	
		this.not_null = row[3] === 1;
		this.primary_key = row[5] === 1;
		this.order = row[0];
		this.geometry_type = null;
	}

    getName() {
     	return this.name;
    }	
    
    getPropertyType() {
        return this.property_type || -1;
    }
    
    isNotNull() {
        return this.not_null;
    }
    
    isPrimaryKey() { 
        return this.primary_key;
    }
    
    isGeometry() {
    	return this.property_type === GEOMETRY;
    }
    
    getGeometryType() {
    	return this.geometry_type;
    }
    
    getOrder() {
    	return this.order;
    }
    
    updateGeometryType(geometryType) { 
    	this.geometry_type = geometryType;
    }
}

class FeatureType {

	constructor(table_name, data_type, identifier, description, last_change) {
		this.table_name = table_name;
		this.data_type = data_type;	
		this.identifier = identifier;
		this.description = description;
		this.last_change = last_change;
		this.properties = [];
	}

    getTableName() {
     	return this.table_name;
    }	
    
    isFeature() {
    	return this.data_type === 'feature';
    }
    
    getIdentifier() {
        return this.identifier;
    }
    
    getDescription() {
        return this.description;
    }
    
    getLastChange() { 
        return this.last_change;
    }
    
    getProperties() {
    	return this.properties;
    }
        
    add(property) {
   		this.properties.push(property);
    }

}

const getRecord = ({values}, record, index) => {
	if( values && record < values.length ) {
		return values[record][index];
	}	
}

const getPropertyType = dataType => {
	const index = dataType.indexOf('(');
	
	let type = dataType.toUpperCase();
	if( index > 0 ) {
		type = type.substring(0, index);	
	}
	
	switch( type ) {
		case "BOOLEAN":
			return BOOLEAN;
		case "DATE":
			return DATE;
		case "DATETIME":
		case "TIMESTAMP WITH TIME ZONE":
			return DATETIME;
		case "REAL":
		case "DOUBLE":
			return FLOAT;
		case "FLOAT":
			return FLOAT;
		case "NUMERIC":
			return DECIMAL;
		case "INT":
		case "INTEGER":
		case "MEDIUMINT":
		case "INT8":
			return INTEGER;
		case "TEXT":
			return STRING;
		case "GEOMETRY":
		case "POLYGON":
		case "POINT":
		case "LINESTRING":
		case "MULTIPOLYGON":
		case "MULTIPOINT":
		case "MULTILINESTRING":
			return GEOMETRY;

	}
	
	console.warn("Unknown property type: ", type);	
	return null;	
}

const createProperty = (db, feature_type, row) => {
	const property = new FeatureProperty(row);
	if( property.isGeometry() ) {
		const response = db.exec(`
       			select geometry_type_name
       			from gpkg_geometry_columns
       			where table_name = '${feature_type.table_name}'
       			and column_name = '${property.name}'
		`);
		
		if( response.length == 1 ) {
			const rows = response[0].values;
			const geometryType = getGeometryType(rows[0][0]);
			property.updateGeometryType( geometryType );	
		}		
	}
	
	return property;
}

const readFields = (db, feature_type) => {
let response;
	try {
	response = db.exec(`
       PRAGMA table_info("${feature_type.table_name}");
   `);
   } catch( e ) {
   	console.log(e);
   }
     
    const rows = response[0].values;
   
	rows.map(row => createProperty(db, feature_type, row))
	.forEach(property => feature_type.add(property));
		
    return feature_type;
}

const readFeatureTypes = (db) => {
	const response = db.exec(`
		select table_name, data_type, identifier, description, last_change
		from gpkg_contents;
	`);
	
	const rows = response[0].values;
	const featureTypes = rows.map(row => new FeatureType(row[0], row[1], row[2], row[3], row[4]))
	.map(featureType => readFields(db, featureType));
		
	return featureTypes;
}

const getGeoPackageContent = (db) => {
	const response = db.exec(`
		PRAGMA application_id;
		PRAGMA user_version;
		SELECT name FROM sqlite_master
		WHERE type='table'
		AND name='gpkg_contents'; 
	`);
	
	let content = null;
	if( response && response.length == 3 ) {
		const applicationId = getRecord(response[0], 0, 0);
		const userVersion = getRecord(response[1], 0, 0);
		console.log("GeoPackage Version: ", userVersion);
				
		if( applicationId === GEOPACKAGE_APPLICATION_ID ) {
			content = readFeatureTypes(db);		
		} else {
			console.warn("Invalid application id: ", applicationId);
		}
	}
	
	return content;
}

const geopackage = async (reader) => {	
	const createSqlWasm = await import(/* webpackChunkName: "geopackage" */ 'sql-wasm').then(module => module.default)	
	const sql = await createSqlWasm({wasmUrl: `${wasmUrl}`});

	const buffer = await reader.read();

	const db = new sql.Database(new Uint8Array(buffer));
	const content = getGeoPackageContent(db);
	db.close();
		
	return content;
}

export {
	geopackage
}
